import styled, { css } from 'styled-components'
import { ChecklistItemStatus, ProductApprovalStatus } from './types'

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: stretch;
  justify-content: center;
  z-index: 1100;
  padding: 32px;
`

export const ModalShell = styled.div`
  background: #f4f6f8;
  width: 100%;
  max-width: 1100px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
`

export const ModalHeader = styled.div`
  background: #fff;
  padding: 18px 24px;
  display: flex;
  align-items: center;
  gap: 14px;
  border-bottom: 1px solid #e5e7eb;
`

export const BackButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #4b5563;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 6px;

  &:hover {
    background: #f3f4f6;
  }
`

export const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

export const HeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

export const HeaderProductName = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`

export const HeaderApprovalName = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #111827;

  &::before {
    content: '— ';
    color: #6b7280;
    font-weight: 400;
  }
`

export const HeaderRef = styled.span`
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
`

export const HeaderSubtitle = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
  letter-spacing: 0.3px;
`

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const baseButton = css`
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  cursor: pointer;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

export const PrimaryButton = styled.button`
  ${baseButton}
  background: #0090d3;
  color: #fff;

  &:hover:not(:disabled) {
    background: #0079b1;
  }
`

export const OutlineButton = styled.button`
  ${baseButton}
  background: #fff;
  color: #1f2937;
  border-color: #d1d5db;

  &:hover:not(:disabled) {
    border-color: #9ca3af;
    background: #f9fafb;
  }
`

export const DangerButton = styled.button`
  ${baseButton}
  background: #fff;
  color: #b91c1c;
  border-color: #fca5a5;

  &:hover:not(:disabled) {
    background: #fef2f2;
  }
`

export const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 22px;
`

export const Card = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 18px 22px;
  border: 1px solid #e5e7eb;
`

export const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 1px;
  margin-bottom: 12px;
`

export const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px 22px;
`

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const Label = styled.label`
  font-size: 11px;
  color: #6b7280;
  font-weight: 600;
  letter-spacing: 0.4px;
`

export const Input = styled.input`
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  color: #111827;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #0090d3;
  }

  &:disabled {
    background: #f3f4f6;
    color: #6b7280;
  }
`

export const TextArea = styled.textarea`
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #111827;
  background: #fff;
  resize: vertical;
  min-height: 60px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #0090d3;
  }

  &:disabled {
    background: #f3f4f6;
    color: #6b7280;
  }
`

export const ImagesRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
`

export const ImageBox = styled.div<{ $hasImage: boolean; $disabled: boolean }>`
  position: relative;
  height: 220px;
  border-radius: 10px;
  background: #f3f4f6;
  border: 1.5px dashed #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};

  ${({ $hasImage }) =>
    $hasImage &&
    css`
      border-style: solid;
      background: #111827;
    `}

  &:hover ${({ $disabled }) => ($disabled ? '' : '> div.overlay')} {
    opacity: ${({ $disabled }) => ($disabled ? 0 : 1)};
  }
`

export const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

export const ImageLabel = styled.span`
  position: absolute;
  bottom: 10px;
  left: 12px;
  background: rgba(15, 23, 42, 0.75);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 3px 10px;
  border-radius: 100px;
  text-transform: uppercase;
`

export const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.15s ease;
  color: #fff;
`

export const ImageEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #9ca3af;
  font-size: 12px;
  font-weight: 500;
`

const statusBadgeColors: Record<ProductApprovalStatus, { bg: string; fg: string }> = {
  IN_PROGRESS: { bg: '#fef3c7', fg: '#92400e' },
  APPROVED: { bg: '#d1fae5', fg: '#065f46' },
  REJECTED: { bg: '#fee2e2', fg: '#991b1b' },
}

export const StatusBadge = styled.span<{ $status: ProductApprovalStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $status }) => statusBadgeColors[$status].bg};
  color: ${({ $status }) => statusBadgeColors[$status].fg};
  letter-spacing: 0.3px;
  text-transform: uppercase;
`

const itemBadgeColors: Record<ChecklistItemStatus, { bg: string; fg: string; border: string }> = {
  PENDING: { bg: '#fff', fg: '#6b7280', border: '#d1d5db' },
  APPROVED: { bg: '#d1fae5', fg: '#065f46', border: '#6ee7b7' },
  REJECTED: { bg: '#fee2e2', fg: '#991b1b', border: '#fca5a5' },
}

export const ItemStatusBadge = styled.span<{ $status: ChecklistItemStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 100px;
  font-size: 10px;
  font-weight: 700;
  background: ${({ $status }) => itemBadgeColors[$status].bg};
  color: ${({ $status }) => itemBadgeColors[$status].fg};
  border: 1px solid ${({ $status }) => itemBadgeColors[$status].border};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`

export const ChecklistList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const ChecklistRow = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
`

export const ChecklistRowHeader = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`

export const ChecklistRowIndex = styled.span`
  font-size: 12px;
  color: #9ca3af;
  font-weight: 600;
  min-width: 24px;
`

export const ChecklistRowDescription = styled.div`
  flex: 1;
  font-size: 14px;
  color: #111827;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`

export const ChecklistRowMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  font-size: 11px;
  color: #6b7280;
`

export const ChecklistRowMetaLabel = styled.span`
  font-size: 9px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #9ca3af;
`

export const ChecklistRowMetaValue = styled.span`
  font-size: 12px;
  color: #1f2937;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

export const VoceTag = styled.span`
  background: #ede9fe;
  color: #6d28d9;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
  letter-spacing: 0.4px;
`

export const KebabWrapper = styled.div`
  position: relative;
  display: inline-flex;
`

export const KebabButton = styled.button`
  background: none;
  border: none;
  padding: 6px;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
`

export const KebabMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 180px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  padding: 4px;
  z-index: 10;
  display: flex;
  flex-direction: column;
`

export const KebabMenuItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: none;
  background: none;
  text-align: left;
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
  color: ${({ $danger }) => ($danger ? '#b91c1c' : '#1f2937')};
  &:hover {
    background: ${({ $danger }) => ($danger ? '#fee2e2' : '#f3f4f6')};
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    background: none !important;
  }
`

export const KebabSeparator = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 4px 2px;
`

export const ChecklistRowBody = styled.div`
  border-top: 1px solid #e5e7eb;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: #fff;
`

export const DecisionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`

export const ApproveButton = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => ($active ? '#10b981' : '#ecfdf5')};
  border: 1px solid ${({ $active }) => ($active ? '#10b981' : '#a7f3d0')};
  color: ${({ $active }) => ($active ? '#fff' : '#047857')};
  border-radius: 8px;
  padding: 10px 14px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover:not(:disabled) {
    background: ${({ $active }) => ($active ? '#0e9c6e' : '#d1fae5')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const RejectButton = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => ($active ? '#ef4444' : '#fef2f2')};
  border: 1px solid ${({ $active }) => ($active ? '#ef4444' : '#fecaca')};
  color: ${({ $active }) => ($active ? '#fff' : '#b91c1c')};
  border-radius: 8px;
  padding: 10px 14px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover:not(:disabled) {
    background: ${({ $active }) => ($active ? '#dc2626' : '#fee2e2')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const FilesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

export const FileThumb = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

export const FileNonImage = styled.div`
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 9px;
  font-weight: 600;
  color: #6b7280;
  text-align: center;
  padding: 6px;

  span {
    word-break: break-all;
    line-height: 1.1;
  }
`

export const RemoveFileBtn = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  background: rgba(15, 23, 42, 0.75);
  border: none;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: #fff;
`

export const AddFileBtn = styled.div`
  width: 72px;
  height: 72px;
  border: 1.5px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #9ca3af;

  &:hover {
    border-color: #0090d3;
    color: #0090d3;
  }
`

export const CommentsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

export const CommentsHeader = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 1px;
  text-transform: uppercase;
`

export const CommentBubble = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f9fafb;
`

export const CommentAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #0090d3;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  text-transform: uppercase;
`

export const CommentContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  color: #1f2937;
`

export const CommentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #6b7280;

  strong {
    color: #111827;
    font-weight: 700;
    font-size: 12px;
  }
`

export const NewCommentRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

export const NewCommentInput = styled.input`
  flex: 1;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 13px;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #0090d3;
  }
`

export const AddItemButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #0090d3;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`

/* ApprovationsTable styles */

export const TableWrapper = styled.div`
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

export const Th = styled.th`
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 14px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
`

export const Tr = styled.tr<{ $clickable?: boolean }>`
  border-bottom: 1px solid #f3f4f6;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $clickable }) => ($clickable ? '#f9fafb' : 'transparent')};
  }
`

export const Td = styled.td`
  padding: 12px 14px;
  color: #1f2937;
  font-size: 13px;
  vertical-align: middle;
`

/** Scroll horizontal + mesma moldura da TableWrapper (Medidas). */
export const MeasuresTableWrapper = styled.div`
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #d1d5db;
    border-radius: 4px;
  }
`

/** border-collapse: separate melhora sticky da primeira coluna. */
export const MeasuresTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
`

export const StickyTh = styled(Th)`
  position: sticky;
  left: 0;
  z-index: 2;
  box-shadow: 1px 0 0 #f3f4f6;
`

export const StickyTd = styled(Td)`
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
  font-weight: 700;
  box-shadow: 1px 0 0 #f3f4f6;
`

/** Célula só como moldura: o input ocupa todo o espaço visível. */
export const MeasuresInputTd = styled(Td)`
  padding: 0;
  vertical-align: middle;
`

export const MeasuresCellInput = styled.input`
  display: block;
  width: 100%;
  min-width: 64px;
  box-sizing: border-box;
  margin: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  padding: 12px 14px;
  font-size: 13px;
  font-family: inherit;
  font-weight: 400;
  color: #1f2937;
  outline: none;
  appearance: textfield;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    background: #f9fafb;
    box-shadow: inset 0 0 0 1px #0090d3;
  }

  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
`

export const EmptyState = styled.div`
  padding: 36px 14px;
  text-align: center;
  color: #6b7280;
  font-size: 13px;
`

export const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 12px;
`

export const NewApprovalForm = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const InlineFormRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
  flex-wrap: wrap;
`
